import type { Dispatch, RefObject, SetStateAction } from "react"
import { CsvImportDialog, type TableField } from "../CsvImportDialog"
import { ExcelBulkUpdateDialog } from "../ExcelBulkUpdateDialog"
import { DelegateItemOwnerDialog } from "../DelegateItemOwnerDialog"
import { ComponentTableActivityLogDialog } from "./ComponentTableActivityLogDialog"
import type { ComponentTableActivityLogDialogProps } from "./ComponentTableActivityLogDialog"
import { ComponentTableColumnOrderDialog } from "./ComponentTableColumnOrderDialog"
import type { ColumnOrderDragState } from "./ComponentTableColumnOrderDialog"
import { ComponentTableCsvFilterDialog } from "./ComponentTableCsvFilterDialog"
import { ComponentTableDebugJsonDialog } from "./ComponentTableDebugJsonDialog"
import { ComponentTableExportDialog } from "./ComponentTableExportDialog"
import type { ExportFieldOption, ExportParams } from "./ComponentTableExportDialog"
import { ComponentTableDuplicatesDialog } from "./ComponentTableDuplicatesDialog"
import { ComponentTableCompareDialog } from "./ComponentTableCompareDialog"
import type { DuplicateFilterEntry } from "./useComponentTableFilters"
import { ComponentTableRelationInspectDialog } from "./ComponentTableRelationInspectDialog"

export type ComponentTableDataDialogsProps = {
  debugMode: boolean
  showJsonDialog: boolean
  setShowJsonDialog: (v: boolean) => void
  displayName: string
  storeJson: string
  clientJson: string

  relationDialogOpen: boolean
  setRelationDialogOpen: (v: boolean) => void
  relationDialogTitle: string
  relationDialogLoading: boolean
  relationDialogData: unknown

  delegateTargetItem: { uid?: string } | null
  setDelegateTargetItem: (v: unknown | null) => void
  currentSlug: string
  onOwnerDelegationChanged: () => void

  csvDialogOpen: boolean
  handleCsvDialogOpenChange: (open: boolean) => void
  csvDialogFieldName: string | null
  csvDialogInput: string
  setCsvDialogInput: (v: string) => void
  clearCsvForField: (fieldName: string) => void
  applyCsvDialog: () => void

  columnOrderDialogOpen: boolean
  handleColumnOrderDialogOpenChange: (open: boolean) => void
  currentColumnOrderDraft: string[]
  reorderableColumnIds: string[]
  defaultColumnOrderIds: string[]
  columnOrderScrollRef: RefObject<HTMLDivElement | null>
  draggingColumnId: string | null
  setDraggingColumnId: (id: string | null) => void
  dragOverState: ColumnOrderDragState
  setDragOverState: (state: ColumnOrderDragState) => void
  setColumnOrderDraft: Dispatch<SetStateAction<string[] | null>>
  moveColumnInDraft: (id: string, direction: "up" | "down") => void
  handleColumnDrop: (sourceId: string, targetId: string) => void
  handleResetColumnOrder: () => void
  handleSaveColumnOrder: () => void
  columnHiddenDraft: string[]
  toggleColumnHiddenInDraft: (columnId: string) => void
  columnOrderVisibleCount: number
  fieldLabelByName: Record<string, string>

  exportDialogOpen: boolean
  setExportDialogOpen: (v: boolean) => void
  exportLoading: boolean
  exportParams: ExportParams
  exportAllFields: boolean
  setExportAllFields: (v: boolean) => void
  exportSelectedFields: string[]
  setExportSelectedFields: Dispatch<SetStateAction<string[]>>
  exportSemicolonField: string
  setExportSemicolonField: (v: string) => void
  exportableFieldsFromMeta: ExportFieldOption[]
  collectionHasRelationFields: boolean
  exportRelationMode: "labels" | "ids"
  setExportRelationMode: (v: "labels" | "ids") => void
  confirmExport: () => void

  importDialogOpen: boolean
  setImportDialogOpen: (v: boolean) => void
  importExcelFields: unknown[] | undefined
  /** Organization segment for org-scoped collection import API calls. */
  importOrganization?: string
  refreshList: () => void

  excelBulkUpdateOpen: boolean
  setExcelBulkUpdateOpen: (v: boolean) => void

  activityLog: Omit<
    ComponentTableActivityLogDialogProps,
    "open" | "onOpenChange" | "stackedItemDetailsOpen" | "onDismissStackedItemDetails"
  >
  activityLogOpen: boolean
  setActivityLogOpen: (v: boolean) => void
  activityLogStackedItemDetailsOpen: boolean
  onDismissActivityLogStackedItemDetails: () => void

  duplicatesDialogOpen: boolean
  setDuplicatesDialogOpen: (v: boolean) => void
  duplicatesDialogRows: Record<string, unknown>[]
  duplicatesMeta: unknown
  duplicatesCurrentMeta: unknown
  onApplyDuplicateFilters: (entries: DuplicateFilterEntry[]) => void

  compareDialogOpen: boolean
  setCompareDialogOpen: (v: boolean) => void
  compareDialogRows: Record<string, unknown>[]
}

export function ComponentTableDataDialogs(props: ComponentTableDataDialogsProps) {
  const {
    debugMode,
    showJsonDialog,
    setShowJsonDialog,
    displayName,
    storeJson,
    clientJson,
    relationDialogOpen,
    setRelationDialogOpen,
    relationDialogTitle,
    relationDialogLoading,
    relationDialogData,
    delegateTargetItem,
    setDelegateTargetItem,
    currentSlug,
    onOwnerDelegationChanged,
    csvDialogOpen,
    handleCsvDialogOpenChange,
    csvDialogFieldName,
    csvDialogInput,
    setCsvDialogInput,
    clearCsvForField,
    applyCsvDialog,
    columnOrderDialogOpen,
    handleColumnOrderDialogOpenChange,
    currentColumnOrderDraft,
    reorderableColumnIds,
    defaultColumnOrderIds,
    columnOrderScrollRef,
    draggingColumnId,
    setDraggingColumnId,
    dragOverState,
    setDragOverState,
    setColumnOrderDraft,
    moveColumnInDraft,
    handleColumnDrop,
    handleResetColumnOrder,
    handleSaveColumnOrder,
    columnHiddenDraft,
    toggleColumnHiddenInDraft,
    columnOrderVisibleCount,
    fieldLabelByName,
    exportDialogOpen,
    setExportDialogOpen,
    exportLoading,
    exportParams,
    exportAllFields,
    setExportAllFields,
    exportSelectedFields,
    setExportSelectedFields,
    exportSemicolonField,
    setExportSemicolonField,
    exportableFieldsFromMeta,
    collectionHasRelationFields,
    exportRelationMode,
    setExportRelationMode,
    confirmExport,
    importDialogOpen,
    setImportDialogOpen,
    importExcelFields,
    importOrganization,
    refreshList,
    excelBulkUpdateOpen,
    setExcelBulkUpdateOpen,
    activityLog,
    activityLogOpen,
    setActivityLogOpen,
    activityLogStackedItemDetailsOpen,
    onDismissActivityLogStackedItemDetails,
    duplicatesDialogOpen,
    setDuplicatesDialogOpen,
    duplicatesDialogRows,
    duplicatesMeta,
    duplicatesCurrentMeta,
    onApplyDuplicateFilters,
    compareDialogOpen,
    setCompareDialogOpen,
    compareDialogRows,
  } = props

  return (
    <>
      <ComponentTableDebugJsonDialog
        open={debugMode && showJsonDialog}
        onOpenChange={setShowJsonDialog}
        displayName={displayName}
        storeJson={storeJson}
        clientJson={clientJson}
      />

      <ComponentTableRelationInspectDialog
        open={relationDialogOpen}
        onOpenChange={setRelationDialogOpen}
        title={relationDialogTitle}
        loading={relationDialogLoading}
        data={relationDialogData}
      />

      <DelegateItemOwnerDialog
        open={Boolean(delegateTargetItem)}
        onOpenChange={(open) => {
          if (!open) setDelegateTargetItem(null)
        }}
        collectionSlug={currentSlug || null}
        itemUid={delegateTargetItem?.uid ?? null}
        onSuccess={onOwnerDelegationChanged}
      />

      <ComponentTableCsvFilterDialog
        open={csvDialogOpen}
        onOpenChange={handleCsvDialogOpenChange}
        fieldName={csvDialogFieldName}
        inputValue={csvDialogInput}
        onInputChange={setCsvDialogInput}
        onClearField={() => {
          if (csvDialogFieldName) clearCsvForField(csvDialogFieldName)
          handleCsvDialogOpenChange(false)
        }}
        onApply={applyCsvDialog}
      />

      <ComponentTableColumnOrderDialog
        open={columnOrderDialogOpen}
        onOpenChange={handleColumnOrderDialogOpenChange}
        currentColumnOrderDraft={currentColumnOrderDraft}
        reorderableColumnIds={reorderableColumnIds}
        defaultColumnOrderIds={defaultColumnOrderIds}
        columnOrderScrollRef={columnOrderScrollRef}
        draggingColumnId={draggingColumnId}
        setDraggingColumnId={setDraggingColumnId}
        dragOverState={dragOverState}
        setDragOverState={setDragOverState}
        setColumnOrderDraft={setColumnOrderDraft}
        moveColumnInDraft={moveColumnInDraft}
        handleColumnDrop={handleColumnDrop}
        onReset={handleResetColumnOrder}
        onSave={handleSaveColumnOrder}
        columnHiddenDraft={columnHiddenDraft}
        toggleColumnHiddenInDraft={toggleColumnHiddenInDraft}
        columnOrderVisibleCount={columnOrderVisibleCount}
        fieldLabelByName={fieldLabelByName}
      />

      <ComponentTableExportDialog
        open={exportDialogOpen}
        onOpenChange={(open) => {
          if (!exportLoading) setExportDialogOpen(open)
        }}
        exportLoading={exportLoading}
        exportParams={exportParams}
        exportAllFields={exportAllFields}
        onExportAllFieldsChange={setExportAllFields}
        exportSelectedFields={exportSelectedFields}
        onExportSelectedFieldsChange={(updater) => setExportSelectedFields(updater)}
        exportSemicolonField={exportSemicolonField}
        onExportSemicolonFieldChange={setExportSemicolonField}
        exportableFieldsFromMeta={exportableFieldsFromMeta}
        collectionHasRelationFields={collectionHasRelationFields}
        exportRelationMode={exportRelationMode}
        onExportRelationModeChange={setExportRelationMode}
        onCancel={() => setExportDialogOpen(false)}
        onConfirmExport={confirmExport}
      />

      {currentSlug && importExcelFields && (
        <CsvImportDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          slug={currentSlug}
          fields={importExcelFields as TableField[]}
          onImported={refreshList}
          organization={importOrganization}
        />
      )}

      {currentSlug && importExcelFields && (
        <ExcelBulkUpdateDialog
          open={excelBulkUpdateOpen}
          onOpenChange={setExcelBulkUpdateOpen}
          slug={currentSlug}
          fields={importExcelFields as TableField[]}
          onApplied={refreshList}
        />
      )}

      <ComponentTableActivityLogDialog
        open={activityLogOpen}
        onOpenChange={setActivityLogOpen}
        stackedItemDetailsOpen={activityLogStackedItemDetailsOpen}
        onDismissStackedItemDetails={onDismissActivityLogStackedItemDetails}
        {...activityLog}
      />

      <ComponentTableDuplicatesDialog
        open={duplicatesDialogOpen}
        onOpenChange={setDuplicatesDialogOpen}
        rows={duplicatesDialogRows}
        exportableFieldsFromMeta={exportableFieldsFromMeta}
        meta={duplicatesMeta}
        currentMeta={duplicatesCurrentMeta}
        onApplyDuplicateFilters={onApplyDuplicateFilters}
      />

      <ComponentTableCompareDialog
        open={compareDialogOpen}
        onOpenChange={setCompareDialogOpen}
        rows={compareDialogRows}
        exportableFieldsFromMeta={exportableFieldsFromMeta}
        meta={duplicatesMeta}
        currentMeta={duplicatesCurrentMeta}
      />
    </>
  )
}
